import { useCallback, useEffect, useState } from 'react';
import { ALFEN_SYSTEM_STATUSES, type WatchMessages } from '../../hooks/useWatchSocket';
import { isMobileXBattery } from '../../utils/hmi.utils';
import { CloseAcBreakerStep } from './CloseAcBreakerStep';
import { ResetRequiredStep } from './ResetRequiredStep';
import { StartBatteryStep } from './StartBatteryStep';
import type { StartupStep, StartupWizardProps } from './StartupWizard.types';

const isResetCleared = (messages: WatchMessages) => {
  const fields = messages['components/container_data']?.payload.fields;
  return fields?.is_process_stop_active !== true && fields?.is_fire_alarm_active !== true;
};

const isAcBreakerClosed = (messages: WatchMessages) =>
  messages['components/input_data']?.payload.fields?.is_breaker_closed === true;

const areHvBatteriesOn = (messages: WatchMessages) =>
  messages.state?.payload.state?.system_status === ALFEN_SYSTEM_STATUSES.ON;

const STARTUP_STEPS: StartupStep[] = [
  {
    id: 'resetRequired',
    isDone: isResetCleared,
    Component: ResetRequiredStep,
  },
  {
    id: 'closeAcBreaker',
    isApplicable: (messages) =>
      !isMobileXBattery(messages.metadata?.payload.asset_major_version) && !isAcBreakerClosed(messages),
    isDone: isAcBreakerClosed,
    Component: CloseAcBreakerStep,
  },
  {
    id: 'startBattery',
    isApplicable: (messages) => !areHvBatteriesOn(messages),
    isDone: () => false,
    Component: StartBatteryStep,
  },
];

export const StartupWizard = ({
  messages,
  steps = STARTUP_STEPS,
  isMobile = false,
  onTurnOnBatteries,
}: StartupWizardProps) => {
  const [visibleSteps] = useState(() => steps.filter((step) => step.isApplicable?.(messages) ?? true));
  const [activeIndex, setActiveIndex] = useState(0);

  const activeStep: StartupStep | undefined = visibleSteps[activeIndex];
  const isActiveStepDone = activeStep?.isDone(messages) ?? false;

  const handleNext = useCallback(() => setActiveIndex((current) => current + 1), []);

  useEffect(() => {
    if (isActiveStepDone) {
      handleNext();
    }
  }, [isActiveStepDone, activeIndex, handleNext]);

  if (!activeStep) {
    return null;
  }

  const { Component: StepComponent } = activeStep;

  return (
    <StepComponent messages={messages} onNext={handleNext} onTurnOnBatteries={onTurnOnBatteries} isMobile={isMobile} />
  );
};
